import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/shadcn/collapsible'
import { Button } from '@/components/shadcn/button'
import { cn } from '@/lib/utils'
import { IconDocumentOutline } from '@icons/IconDocumentOutline'
import { IconRightChevronOutline } from '@icons/IconRightChevronOutline'
import { IconV2DownloadOutline } from '@icons/IconV2DownloadOutline'
import { IconV2FolderOutline } from '@icons/IconV2FolderOutline'
import { IconV2TrashOutline } from '@icons/IconV2TrashOutline'
import { IconV2UploadOutline } from '@icons/IconV2UploadOutline'
import { useRef, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient } from '@/contexts/QueryProvider'
import { toast } from 'sonner'
import { ENDPOINTS } from '@/util'
import { downloadSharedFile } from './downloadSharedFile'
import type { ApplicationFileTreeFileNode, ApplicationFileTreeFolderNode, ApplicationFileTreeNode } from './applicationFileTree'

const treeTextClass = 'text-base'
const treeLeadingIconClass = 'text-muted-foreground size-5 shrink-0'
const treeChevronClass = 'text-muted-foreground size-4 shrink-0 transition-transform duration-200'
const treeRowButtonClass =
  'h-auto min-h-9 w-full justify-start gap-2.5 px-2 py-2 text-base font-normal [&_svg:not([class*="size-"])]:size-5'
const treeActionIconClass = 'size-5'

export type ApplicationFileTreeViewProps = {
  nodes: ApplicationFileTreeNode[]
  appKey: string
  className?: string
  nested?: boolean
  onRefresh: () => void
  /** When true, hides upload and delete actions (workspace read-only view) */
  readOnly?: boolean
}

export function ApplicationFileTreeView({
  nodes,
  appKey,
  className,
  nested = false,
  onRefresh,
  readOnly = false,
}: ApplicationFileTreeViewProps) {
  if (nodes.length === 0) {
    return <p className={cn('px-1 py-6 text-muted-foreground', treeTextClass)}>No shared files to show.</p>
  }

  const defaultFolderOpen = !nested

  return (
    <ul className={cn('m-0 list-none space-y-0.5 p-0', treeTextClass, className)} role={nested ? 'group' : 'tree'}>
      {nodes.map((node) => (
        <ApplicationFileTreeItem
          key={node.nodeKind === 'folder' ? node.path : node.blobName}
          node={node}
          appKey={appKey}
          defaultFolderOpen={defaultFolderOpen}
          onRefresh={onRefresh}
          readOnly={readOnly}
        />
      ))}
    </ul>
  )
}

function ApplicationFileTreeItem({
  node,
  appKey,
  defaultFolderOpen,
  onRefresh,
  readOnly,
}: {
  node: ApplicationFileTreeNode
  appKey: string
  defaultFolderOpen: boolean
  onRefresh: () => void
  readOnly: boolean
}) {
  if (node.nodeKind === 'file') {
    return <ApplicationFileTreeFileRow node={node} appKey={appKey} onRefresh={onRefresh} readOnly={readOnly} />
  }
  return (
    <ApplicationFileTreeFolderRow
      node={node}
      appKey={appKey}
      defaultFolderOpen={defaultFolderOpen}
      onRefresh={onRefresh}
      readOnly={readOnly}
    />
  )
}

function ApplicationFileTreeFileRow({
  node,
  appKey,
  onRefresh,
  readOnly,
}: {
  node: ApplicationFileTreeFileNode
  appKey: string
  onRefresh: () => void
  readOnly: boolean
}) {
  return (
    <li className="m-0 flex items-stretch gap-1 p-0 pr-1" role="treeitem">
      <div className="min-w-0 flex-1">
        <div className={cn(treeRowButtonClass, 'flex cursor-default items-start rounded-md')}>
          <IconDocumentOutline className={cn(treeLeadingIconClass, 'mt-0.5')} />
          <span className="min-w-0 flex-1 truncate font-normal">{node.fileName}</span>
        </div>
      </div>
      <ApplicationFileTreeFileActions node={node} appKey={appKey} onRefresh={onRefresh} readOnly={readOnly} />
    </li>
  )
}

function ApplicationFileTreeFileActions({
  node,
  appKey,
  onRefresh,
  readOnly,
}: {
  node: ApplicationFileTreeFileNode
  appKey: string
  onRefresh: () => void
  readOnly: boolean
}) {
  const { deleteStorageApiDeleteSharedFile } = useRestClient()
  const { mutate: deleteFile, isPending: isDeleteFilePending } = deleteStorageApiDeleteSharedFile.useMutation()

  const onDeleteConfirm = () => {
    deleteFile(
      {
        body: {
          appKey,
          namespace: node.namespace ?? undefined,
          filename: node.fileName ?? '',
        },
      },
      {
        onSuccess: () => {
          toast.success('File deleted.')
          onRefresh()
        },
        onError: () => {
          toast.error('File was not deleted.')
        },
      }
    )
  }

  return (
    <div
      className="flex shrink-0 items-center gap-0.5 self-center"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Download file"
            onClick={() => downloadSharedFile(node, appKey)}
          >
            <IconV2DownloadOutline className={treeActionIconClass} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={6}>
          Download
        </TooltipContent>
      </Tooltip>
      {!readOnly && (
        <ConfirmDialog
          triggerTooltip="Delete"
          triggerTooltipSide="top"
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={isDeleteFilePending}
              aria-label="Delete file"
            >
              <IconV2TrashOutline className={treeActionIconClass} />
            </Button>
          }
          confirmVariant="destructive"
          confirmLabel="Delete"
          onConfirm={onDeleteConfirm}
          title="Delete file"
          description="Are you sure you want to delete the file?"
        />
      )}
    </div>
  )
}

function ApplicationFileTreeFolderRow({
  node,
  appKey,
  defaultFolderOpen,
  onRefresh,
  readOnly,
}: {
  node: ApplicationFileTreeFolderNode
  appKey: string
  defaultFolderOpen: boolean
  onRefresh: () => void
  readOnly: boolean
}) {
  const hasChildren = node.children.length > 0

  if (!hasChildren) {
    return (
      <li className="m-0 p-0" role="treeitem">
        <div
          className={cn(
            'flex w-full cursor-default items-center gap-2.5 px-2 py-2 text-muted-foreground',
            treeTextClass
          )}
        >
          <IconV2FolderOutline className={treeLeadingIconClass} />
          <span className="min-w-0 flex-1 truncate text-foreground">{node.name}</span>
          {!readOnly && <FolderUploadButton appKey={appKey} folderPath={node.path} onRefresh={onRefresh} />}
        </div>
      </li>
    )
  }

  return (
    <ApplicationFileTreeFolderBranch
      node={node}
      appKey={appKey}
      defaultOpen={defaultFolderOpen}
      onRefresh={onRefresh}
      readOnly={readOnly}
    />
  )
}

function ApplicationFileTreeFolderBranch({
  node,
  appKey,
  defaultOpen,
  onRefresh,
  readOnly,
}: {
  node: ApplicationFileTreeFolderNode
  appKey: string
  defaultOpen: boolean
  onRefresh: () => void
  readOnly: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <li className="m-0 p-0" role="treeitem" aria-expanded={open}>
      <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
        <div className="flex items-center">
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className={cn(treeRowButtonClass, 'flex-1')}>
              <IconV2FolderOutline className={treeLeadingIconClass} />
              <span className="min-w-0 flex-1 truncate text-left">{node.name}</span>
              <IconRightChevronOutline className={cn(treeChevronClass, open && 'rotate-90')} />
            </Button>
          </CollapsibleTrigger>
          {!readOnly && <FolderUploadButton appKey={appKey} folderPath={node.path} onRefresh={onRefresh} />}
        </div>
        <CollapsibleContent className="overflow-hidden transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <div className="ml-3 border-l border-border pl-2 pt-0.5">
            <ApplicationFileTreeView nested nodes={node.children} appKey={appKey} onRefresh={onRefresh} readOnly={readOnly} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

function FolderUploadButton({
  appKey,
  folderPath,
  onRefresh,
}: {
  appKey: string
  folderPath: string
  onRefresh: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)

  const onFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    const token = JSON.parse(sessionStorage.getItem('token') ?? '')
    try {
      const formData = new FormData()
      formData.append('appKey', appKey)
      formData.append('namespace', folderPath)

      if (files.length === 1) {
        formData.append('file', files[0])
        const response = await fetch(`${ENDPOINTS.LANDING}/storage/upload_shared_file/`, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        })
        if (!response.ok) throw new Error(response.statusText)
        toast.success('File uploaded.')
      } else {
        Array.from(files).forEach((file, i) => formData.append(`file${i + 1}`, file))
        const response = await fetch(`${ENDPOINTS.LANDING}/storage/upload_shared_files/`, {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: formData,
        })
        if (!response.ok) throw new Error(response.statusText)
        toast.success(`${files.length} files uploaded.`)
      }
      onRefresh()
    } catch (error) {
      toast.error('Upload failed.')
      console.error(error)
    } finally {
      setIsUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onFilesSelected} />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground"
            aria-label="Upload files to folder"
            disabled={isUploading}
            onClick={(e) => {
              e.stopPropagation()
              inputRef.current?.click()
            }}
          >
            <IconV2UploadOutline className={treeActionIconClass} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={6}>
          Upload files here
        </TooltipContent>
      </Tooltip>
    </>
  )
}
